import type { AriaRole, ReactNode } from 'react';
import { testIdAttr } from '@src/components/testId';
import { CardDecoration, type DecorationVariant } from '@src/theme/Decoration';
import './Panel.css';

export type PanelPadding = 'none' | 'small' | 'medium' | 'large';

export interface PanelProps {
  children: ReactNode;
  as?: 'div' | 'article' | 'section' | 'details';
  /** Optional outline using the shared surface border color. */
  bordered?: boolean;
  role?: AriaRole;
  'aria-label'?: string;
  /**
   * Large matches the UI kit section cards. None is for when the contents
   * bring their own padding.
   */
  padding?: PanelPadding;
  edges?: { pieces: DecorationVariant };
  /** A light lift, for a panel that should sit above the page rather than in it. */
  elevated?: boolean;
  /** Stretch to the parent width, for a panel in a centred column. */
  wide?: boolean;
  className?: string;
  id?: string;
  testId?: string;
}

export function Panel({
  children,
  as: Component = 'div',
  bordered = false,
  role,
  'aria-label': ariaLabel,
  edges,
  padding = 'large',
  elevated = false,
  wide = false,
  className,
  id,
  testId,
}: PanelProps) {
  const classes = ['panel', `panel--padding-${padding}`];
  if (edges) classes.push('panel--edges');
  if (bordered) classes.push('panel--bordered');
  if (elevated) {
    classes.push('panel--elevated');
  }
  if (wide) {
    classes.push('panel--wide');
  }
  if (className) {
    classes.push(className);
  }

  return (
    <Component
      role={role}
      aria-label={ariaLabel}
      className={classes.join(' ')}
      id={id}
      {...testIdAttr(testId)}
    >
      {edges && <CardDecoration variant={edges.pieces} />}
      {children}
    </Component>
  );
}
